import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Inbox, Check, X, Trash2, Mail, MapPin, Phone, Building, Calendar, ChevronDown, Power, PowerOff, ShieldCheck, KeyRound, LogIn } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { timeAgo, formatAbsolute } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FlaskConical, UserPlus, Link as LinkIcon } from "lucide-react";
import AddDistributorUserDialog from "@/components/AddDistributorUserDialog";
import EditDistributorAccessDialog from "@/components/EditDistributorAccessDialog";
import InviteCoworkerDialog from "@/components/InviteCoworkerDialog";
import CredentialsPanel from "@/components/CredentialsPanel";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "denied", label: "Denied" },
];

export default function ApprovalsPage() {
  const { user } = useAuth();
  const { agencies, activeId } = useAgency();
  const isSuperAdmin = user?.role === "admin";
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, denied: 0 });
  const [decision, setDecision] = useState(null); // { reg, action: "approve" | "deny" }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [addDistOpen, setAddDistOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Credentials returned from an approval: { user, temp_password, already_existed? }
  const [approvalCreds, setApprovalCreds] = useState(null);
  // Distributor user currently being edited for manufacturer access.
  const [accessUser, setAccessUser] = useState(null);

  const load = useCallback(async () => {
    // Fetch distributor-users always so the "Approved" badge shows the real
    // count (which includes manually-added distributors, not just approved
    // registration records). For the approved tab we ALSO use this list as
    // the row data.
    const distParams = isSuperAdmin && activeId ? { agency_id: activeId } : {};
    if (tab === "approved") {
      const [{ data: dists }, { data: c }] = await Promise.all([
        api.get("/distributor-users", { params: distParams }),
        api.get("/registrations/counts"),
      ]);
      setRows(dists);
      setCounts({ ...c, approved: Array.isArray(dists) ? dists.length : 0 });
      return;
    }
    const [{ data: list }, { data: c }, { data: dists }] = await Promise.all([
      api.get("/registrations", { params: { status_filter: tab } }),
      api.get("/registrations/counts"),
      api.get("/distributor-users", { params: distParams }),
    ]);
    setRows(list);
    setCounts({ ...c, approved: Array.isArray(dists) ? dists.length : 0 });
  }, [tab, isSuperAdmin, activeId]);

  useEffect(() => {
    load();
  }, [load]);

  const onDecide = async (reg, status, notes) => {
    try {
      const { data } = await api.patch(`/registrations/${reg.id}`, { status, notes });
      setDecision(null);
      load();
      const du = data?.distributor_user;
      if (status === "approved" && du) {
        if (du.already_existed) {
          toast.success("Approved. Distributor account already existed — no new password generated.");
        } else if (du.temp_password) {
          setApprovalCreds(du);
        } else {
          toast.success("Registration approved");
        }
      } else {
        toast.success(`Registration ${status}`);
      }
    } catch (e) {
      toast.error(formatApiError(e, "Decision failed"));
    }
  };

  const onDelete = async (row) => {
    try {
      if (tab === "approved") {
        // row is a distributor user
        await api.delete(`/distributor-users/${row.id}`);
        toast.success("Distributor removed");
      } else {
        // row is a registration record
        await api.delete(`/registrations/${row.id}`);
        toast.success("Registration deleted");
      }
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-1.5">
            <Inbox className="w-3 h-3" />
            Approvals
          </div>
          <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
            Distributor Registrations
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            {isSuperAdmin
              ? "Review and approve every distributor sign-up across all agencies."
              : "Review and approve distributors who requested access through your agency's app."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setAddDistOpen(true)}
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
            data-testid="add-distributor-btn"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />
            Add distributor user
          </Button>
          <Button
            variant="outline"
            onClick={() => setInviteOpen(true)}
            className="rounded-sm"
            data-testid="invite-link-btn"
          >
            <LinkIcon className="w-3.5 h-3.5 mr-1.5" />
            Invite link
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTestOpen(true)}
            className="rounded-sm border-dashed text-slate-700"
            data-testid="test-registration-btn"
            title="Submit a sample registration into your queue — useful for demos"
          >
            <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
            Test registration
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`relative px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "text-slate-900"
                : "text-muted-foreground hover:text-slate-700"
            }`}
            data-testid={`approvals-tab-${t.key}`}
          >
            {t.label}
            <span
              className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] rounded-sm font-mono-data ${
                tab === t.key
                  ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {counts[t.key] ?? 0}
            </span>
            {tab === t.key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900" />
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {rows === null ? (
        <div className="byrd-progress" />
      ) : rows.length === 0 ? (
        <div
          className="border border-dashed border-border rounded-sm bg-white p-12 text-center"
          data-testid="approvals-empty"
        >
          <Inbox className="w-8 h-8 mx-auto text-slate-400" />
          <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">
            {tab === "pending"
              ? "No pending registrations"
              : tab === "approved"
              ? "No distributors with access yet"
              : "No denied registrations"}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {tab === "pending"
              ? "When a distributor requests access through your app, they'll appear here."
              : tab === "approved"
              ? "Approved registrations and distributors you add manually will appear here so you can manage access, reset passwords, or deactivate them."
              : "Decisions you make will be recorded here for your records."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {tab === "approved"
            ? rows.map((u) => (
                <DistributorUserCard
                  key={u.id}
                  user={u}
                  isSuperAdmin={isSuperAdmin}
                  onManageAccess={() => setAccessUser(u)}
                  onResetPassword={async () => {
                    try {
                      const { data } = await api.post(
                        `/distributor-users/${u.id}/reset-password`
                      );
                      setApprovalCreds(data);
                    } catch (e) {
                      toast.error(formatApiError(e, "Reset failed"));
                    }
                  }}
                  onToggleActive={async () => {
                    try {
                      await api.patch(`/distributor-users/${u.id}/active`, {
                        active: !u.active,
                      });
                      toast.success(u.active ? "Deactivated" : "Reactivated");
                      load();
                    } catch (e) {
                      toast.error(formatApiError(e, "Update failed"));
                    }
                  }}
                  onDelete={() => setConfirmDelete(u)}
                />
              ))
            : rows.map((r) => (
                <RegistrationCard
                  key={r.id}
                  reg={r}
                  isSuperAdmin={isSuperAdmin}
                  onApprove={() => setDecision({ reg: r, action: "approve" })}
                  onDeny={() => setDecision({ reg: r, action: "deny" })}
                  onDelete={() => setConfirmDelete(r)}
                />
              ))}
        </div>
      )}

      <DecisionDialog
        decision={decision}
        onClose={() => setDecision(null)}
        onConfirm={(notes) =>
          onDecide(
            decision.reg,
            decision.action === "approve" ? "approved" : "denied",
            notes
          )
        }
      />

      <TestRegistrationDialog
        open={testOpen}
        onOpenChange={setTestOpen}
        onSubmitted={() => {
          setTab("pending");
          load();
        }}
        isSuperAdmin={isSuperAdmin}
        userAgencyId={user?.agency_id}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tab === "approved" ? "Remove this distributor?" : "Delete this registration?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tab === "approved" ? (
                <>
                  This permanently removes{" "}
                  <strong>
                    {confirmDelete?.first_name} {confirmDelete?.last_name}
                  </strong>{" "}
                  ({confirmDelete?.email}) — they'll lose access to your app
                  immediately. If you just want to pause them, use the deactivate
                  toggle instead.
                </>
              ) : (
                <>
                  This permanently removes the request from{" "}
                  <strong>
                    {confirmDelete?.first_name} {confirmDelete?.last_name}
                  </strong>{" "}
                  ({confirmDelete?.email}). This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDelete(confirmDelete)}
              className="bg-destructive hover:bg-red-600 text-white rounded-sm"
              data-testid="confirm-delete-registration"
            >
              {tab === "approved" ? "Remove" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AddDistributorUserDialog
        open={addDistOpen}
        onOpenChange={setAddDistOpen}
        isSuperAdmin={isSuperAdmin}
        userAgencyId={user?.agency_id}
        agencies={agencies}
        defaultAgencyId={activeId}
        onCreated={load}
      />

      <InviteCoworkerDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        agencyId={isSuperAdmin ? activeId : user?.agency_id}
        agencyName={
          isSuperAdmin
            ? agencies?.find((a) => a.id === activeId)?.name
            : user?.agency_name
        }
        agencies={isSuperAdmin && !activeId ? agencies : undefined}
      />

      <Dialog
        open={!!approvalCreds}
        onOpenChange={(o) => {
          if (!o) setApprovalCreds(null);
        }}
      >
        <DialogContent
          className="rounded-sm max-w-lg"
          data-testid="approval-credentials-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Registration approved
            </DialogTitle>
            <DialogDescription>
              Send these credentials to the distributor. This password is shown
              only once.
            </DialogDescription>
          </DialogHeader>
          {approvalCreds && (
            <CredentialsPanel
              user={approvalCreds.user}
              tempPassword={approvalCreds.temp_password}
              onDone={() => setApprovalCreds(null)}
              onManageAccess={() => {
                const u = approvalCreds.user;
                setApprovalCreds(null);
                setAccessUser(u);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <EditDistributorAccessDialog
        open={!!accessUser}
        onOpenChange={(v) => !v && setAccessUser(null)}
        distributorUser={accessUser}
        onSaved={load}
      />
    </div>
  );
}

function RegistrationCard({ reg, isSuperAdmin, onApprove, onDeny, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const statusStyle =
    reg.status === "approved"
      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
      : reg.status === "denied"
      ? "border-rose-300 bg-rose-50 text-rose-700"
      : "border-amber-300 bg-amber-50 text-amber-700";
  const createdAt = useMemo(() => {
    try {
      return new Date(reg.created_at).toLocaleString();
    } catch {
      return reg.created_at || "";
    }
  }, [reg.created_at]);

  return (
    <div
      className="bg-white border border-border rounded-sm p-4 hover:border-slate-400 transition-colors"
      data-testid={`registration-row-${reg.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base font-bold tracking-tight text-slate-900">
              {reg.first_name} {reg.last_name}
            </div>
            <span
              className={`text-[10px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 rounded-sm border ${statusStyle}`}
            >
              {reg.status}
            </span>
            {isSuperAdmin && reg.agency_name && (
              <span
                className="text-[10px] uppercase tracking-wider font-mono-data text-slate-500"
                data-testid={`registration-agency-${reg.id}`}
              >
                · {reg.agency_name}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-700">
            <Building className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-medium">{reg.distributor_name}</span>
            <span className="text-muted-foreground">·</span>
            <MapPin className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-muted-foreground">{reg.distributor_location}</span>
          </div>
          <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
            <a
              href={`mailto:${reg.email}`}
              className="inline-flex items-center gap-1.5 hover:text-slate-900"
            >
              <Mail className="w-3 h-3" />
              {reg.email}
            </a>
            <a
              href={`tel:${reg.phone}`}
              className="inline-flex items-center gap-1.5 hover:text-slate-900"
            >
              <Phone className="w-3 h-3" />
              {reg.phone}
            </a>
            <span className="inline-flex items-center gap-1.5 font-mono-data">
              <Calendar className="w-3 h-3" />
              {createdAt}
            </span>
          </div>
          {expanded && reg.notes && (
            <div className="mt-3 bg-slate-50 border border-border rounded-sm p-2 text-xs text-slate-700">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data mb-1">
                Decision notes
              </div>
              {reg.notes}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {reg.status === "pending" && (
            <>
              <Button
                size="sm"
                onClick={onApprove}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`approve-btn-${reg.id}`}
              >
                <Check className="w-3.5 h-3.5 mr-1.5" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onDeny}
                className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                data-testid={`deny-btn-${reg.id}`}
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Deny
              </Button>
            </>
          )}
          {reg.notes && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(!expanded)}
              className="rounded-sm h-8"
              data-testid={`notes-toggle-${reg.id}`}
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  expanded ? "rotate-180" : ""
                }`}
              />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="rounded-sm h-8 w-8 p-0 text-rose-600 hover:text-rose-800 hover:bg-rose-50"
            data-testid={`delete-btn-${reg.id}`}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function DistributorUserCard({
  user,
  isSuperAdmin,
  onManageAccess,
  onResetPassword,
  onToggleActive,
  onDelete,
}) {
  const isActive = user.active !== false;
  const createdAt = useMemo(() => {
    try {
      return new Date(user.created_at).toLocaleString();
    } catch {
      return user.created_at || "";
    }
  }, [user.created_at]);

  return (
    <div
      className={`bg-white border rounded-sm p-4 transition-colors ${
        isActive ? "border-border hover:border-slate-400" : "border-slate-200 bg-slate-50/60"
      }`}
      data-testid={`distributor-user-row-${user.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={`font-display text-base font-bold tracking-tight ${
                isActive ? "text-slate-900" : "text-slate-500"
              }`}
            >
              {user.first_name || user.name} {user.last_name || ""}
            </div>
            <span
              className={`text-[10px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 rounded-sm border ${
                isActive
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-slate-300 bg-slate-100 text-slate-600"
              }`}
            >
              {isActive ? "Active" : "Deactivated"}
            </span>
            {isSuperAdmin && user.agency_name && (
              <span className="text-[10px] uppercase tracking-wider font-mono-data text-slate-500">
                · {user.agency_name}
              </span>
            )}
          </div>
          {(user.distributor_name || user.distributor_location) && (
            <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-700">
              <Building className="w-3.5 h-3.5 text-slate-400" />
              <span className="font-medium">{user.distributor_name || "—"}</span>
              {user.distributor_location && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-muted-foreground">
                    {user.distributor_location}
                  </span>
                </>
              )}
            </div>
          )}
          <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
            <a
              href={`mailto:${user.email}`}
              className="inline-flex items-center gap-1.5 hover:text-slate-900"
            >
              <Mail className="w-3 h-3" />
              {user.email}
            </a>
            {user.phone && (
              <a
                href={`tel:${user.phone}`}
                className="inline-flex items-center gap-1.5 hover:text-slate-900"
              >
                <Phone className="w-3 h-3" />
                {user.phone}
              </a>
            )}
            {createdAt && (
              <span
                className="inline-flex items-center gap-1.5 font-mono-data"
                title="Approved / created"
              >
                <Calendar className="w-3 h-3" />
                {createdAt}
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1.5 font-mono-data ${
                user.last_login_at ? "text-slate-700" : "text-muted-foreground"
              }`}
              title={
                user.last_login_at
                  ? `Last login · ${formatAbsolute(user.last_login_at)}`
                  : "Has not signed in yet"
              }
              data-testid={`last-login-${user.id}`}
            >
              <LogIn className="w-3 h-3" />
              {user.last_login_at
                ? `Last login ${timeAgo(user.last_login_at)}`
                : "Never signed in"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={onManageAccess}
            className="rounded-sm h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid={`manage-access-btn-${user.id}`}
            title="Manage manufacturer access"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onResetPassword}
            className="rounded-sm h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid={`reset-password-btn-${user.id}`}
            title="Reset password"
          >
            <KeyRound className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleActive}
            className={`rounded-sm h-8 w-8 p-0 ${
              isActive
                ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                : "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
            }`}
            data-testid={`toggle-active-btn-${user.id}`}
            title={isActive ? "Deactivate" : "Reactivate"}
          >
            {isActive ? (
              <PowerOff className="w-3.5 h-3.5" />
            ) : (
              <Power className="w-3.5 h-3.5" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="rounded-sm h-8 w-8 p-0 text-rose-600 hover:text-rose-800 hover:bg-rose-50"
            data-testid={`delete-distributor-btn-${user.id}`}
            title="Remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}


function DecisionDialog({ decision, onClose, onConfirm }) {
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (decision) {
      setNotes(decision.reg?.notes || "");
    }
  }, [decision]);

  if (!decision) return null;
  const isApprove = decision.action === "approve";
  const r = decision.reg;

  return (
    <Dialog open={!!decision} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {isApprove ? "Approve registration" : "Deny registration"}
          </DialogTitle>
          <DialogDescription>
            {isApprove
              ? "Confirm this distributor should have access to your agency's app."
              : "Capture a reason — this becomes a demand signal for future expansion."}
          </DialogDescription>
        </DialogHeader>
        <div className="bg-slate-50 border border-border rounded-sm p-3 text-sm space-y-1">
          <div className="font-semibold text-slate-900">
            {r.first_name} {r.last_name} — {r.distributor_name}
          </div>
          <div className="text-xs text-muted-foreground">
            {r.distributor_location} · {r.email} · {r.phone}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
            {isApprove ? "Notes (optional)" : "Reason / notes"}
          </label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              isApprove
                ? "e.g., Confirmed long-time customer of TCP via Charles."
                : "e.g., We don't have TCP Lighting in Florida yet."
            }
            className="mt-1 rounded-sm min-h-[88px]"
            data-testid="decision-notes-input"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-sm"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(notes)}
            className={`rounded-sm text-white ${
              isApprove
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-rose-600 hover:bg-rose-700"
            }`}
            data-testid="decision-confirm-btn"
          >
            {isApprove ? "Approve" : "Deny"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SAMPLE_NAMES = [
  ["Sarah", "Mitchell", "Capital Electric Supply", "Charlotte, NC"],
  ["James", "Rodriguez", "Allied Lighting Supply", "Atlanta, GA"],
  ["Megan", "O'Brien", "Tidewater Distributors", "Norfolk, VA"],
  ["David", "Kim", "Piedmont Electric", "Greensboro, NC"],
  ["Aisha", "Patel", "Carolina Power Supply", "Raleigh, NC"],
  ["Marcus", "Thompson", "Southern Lighting Group", "Birmingham, AL"],
];

function randomSample() {
  const [first, last, dist, loc] = SAMPLE_NAMES[Math.floor(Math.random() * SAMPLE_NAMES.length)];
  const slug = (dist || "demo").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const stamp = Date.now().toString(36).slice(-4);
  return {
    first_name: first,
    last_name: last,
    distributor_name: dist,
    distributor_location: loc,
    email: `${first.toLowerCase()}.${last.toLowerCase().replace(/[^a-z]/g, "")}@${slug}.com`,
    phone: `(704) 555-${1000 + Math.floor(Math.random() * 9000)}`,
    _stamp: stamp,
  };
}

function TestRegistrationDialog({ open, onOpenChange, onSubmitted, isSuperAdmin, userAgencyId }) {
  const { agencies, activeId } = useAgency();
  const [agencyId, setAgencyId] = useState("");
  const [form, setForm] = useState(randomSample());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(randomSample());
      // Default agency: agency_user is locked to their own; admin uses the AgencySwitcher's active one
      if (isSuperAdmin) {
        setAgencyId(activeId || (agencies[0]?.id ?? ""));
      } else {
        setAgencyId(userAgencyId || "");
      }
    }
  }, [open, isSuperAdmin, userAgencyId, activeId, agencies]);

  const reshuffle = () => setForm(randomSample());

  const submit = async (e) => {
    e.preventDefault();
    if (!agencyId) {
      toast.error("Pick an agency");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        agency_id: agencyId,
        first_name: form.first_name,
        last_name: form.last_name,
        distributor_name: form.distributor_name,
        distributor_location: form.distributor_location,
        email: form.email,
        phone: form.phone,
      };
      await api.post("/registrations", payload);
      toast.success("Test registration created");
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="test-registration-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-amber-600" />
            Submit a test registration
          </DialogTitle>
          <DialogDescription>
            Drops a fake distributor signup into your queue — handy for demos
            and walkthroughs before the white-labeled apps ship. Uses the same
            public endpoint the apps will call.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {isSuperAdmin && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                Submit to agency *
              </Label>
              <Select value={agencyId} onValueChange={setAgencyId}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="test-agency-select">
                  <SelectValue placeholder="Pick an agency…" />
                </SelectTrigger>
                <SelectContent>
                  {agencies
                    .filter((a) => a.active !== false)
                    .map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                        {a.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                First name
              </Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="mt-1 rounded-sm"
                data-testid="test-first-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                Last name
              </Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="mt-1 rounded-sm"
                data-testid="test-last-name"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Distributor name
            </Label>
            <Input
              value={form.distributor_name}
              onChange={(e) => setForm({ ...form, distributor_name: e.target.value })}
              className="mt-1 rounded-sm"
              data-testid="test-distributor-name"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Distributor location
            </Label>
            <Input
              value={form.distributor_location}
              onChange={(e) => setForm({ ...form, distributor_location: e.target.value })}
              className="mt-1 rounded-sm"
              data-testid="test-distributor-location"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                Email
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 rounded-sm"
                data-testid="test-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                Phone
              </Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 rounded-sm"
                data-testid="test-phone"
              />
            </div>
          </div>

          <DialogFooter className="!justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={reshuffle}
              className="rounded-sm"
              data-testid="test-reshuffle-btn"
            >
              Reshuffle sample
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="test-submit-btn"
              >
                {submitting ? "Submitting…" : "Drop into queue"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

